import React, { useEffect, useState } from "react";
import { Save, X, Check, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

interface FloatingActionsPanelProps {
  isEditing: boolean;
  hasChanges: boolean;
  isSaving: boolean;
  onSave: () => void;
  onCancel: () => void;
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-right-below-header';
  error?: string | null;
  status?: 'idle' | 'saving' | 'saved' | 'error' | 'unsaved';
}

type ActionStatus = 'idle' | 'saving' | 'saved' | 'error' | 'unsaved';

// Константы для позиционирования относительно шапки
const HEADER_HEIGHT = 64; // h-16 = 64px
const DESKTOP_SPACING = 24; // отступ от краев для desktop
const MOBILE_SAFE_AREA = 'env(safe-area-inset-top)';

export const FloatingActionsPanel: React.FC<FloatingActionsPanelProps> = ({
  isEditing,
  hasChanges,
  isSaving,
  onSave,
  onCancel,
  position = 'top-right-below-header',
  error = null,
  status = 'idle'
}) => {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 768;
    }
    return false;
  });

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  if (!isEditing) return null;

  const getStatusIcon = (currentStatus: ActionStatus) => {
    switch (currentStatus) {
      case 'saving':
        return <Loader2 size={16} className="animate-spin" />;
      case 'saved':
        return <Check size={16} className="text-green-600" />;
      case 'error':
        return <AlertCircle size={16} className="text-red-600" />;
      case 'unsaved':
        return <div className="w-2 h-2 bg-yellow-500 rounded-full" />;
      default:
        return null;
    }
  };

  const getStatusText = (currentStatus: ActionStatus) => {
    switch (currentStatus) {
      case 'saving':
        return t('records.floating_panel.saving');
      case 'saved':
        return t('records.floating_panel.saved');
      case 'error':
        return t('records.floating_panel.error');
      case 'unsaved':
        return t('records.floating_panel.unsaved');
      default:
        return '';
    }
  };

  const currentStatus: ActionStatus = error ? 'error' : isSaving ? 'saving' : hasChanges ? 'unsaved' : 'saved';

  const positionClasses = {
    'bottom-right': 'bottom-6 right-6',
    'bottom-left': 'bottom-6 left-6',
    'top-right': 'top-6 right-6',
    'top-right-below-header': 'right-6'
  };

  // Динамически рассчитываем top позицию для избежания конфликта с шапкой
  const topPosition = HEADER_HEIGHT + DESKTOP_SPACING; // 64px + 24px = 88px

  // Mobile layout
  if (isMobile) {
    return (
      <div
        data-testid="floating-actions-panel"
        className="fixed top-0 left-0 right-0 z-[1001] animate-in slide-in-from-top-4 duration-300"
        style={{
          paddingTop: 'env(safe-area-inset-top)',
        }}
      >
        <div className="bg-background/95 backdrop-blur-sm border-b border-border/20 h-16 flex items-center justify-end px-4">

          {/* Error message */}
          {error && (
            <div className="mb-3 p-2 bg-destructive/10 border border-destructive/20 rounded text-xs text-destructive">
              {error}
            </div>
          )}

          {/* Mobile action buttons - aligned to right */}
          <div className="flex gap-3">
            <Button
              className="flex-1 h-12 text-base"
              variant="outline"
              onClick={onCancel}
              disabled={isSaving}
            >
              {t('records.floating_panel.cancel')}
            </Button>
            <Button
              className="flex-1 h-12 text-base"
              onClick={onSave}
              disabled={!hasChanges || isSaving}
            >
              {t('records.floating_panel.save')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Desktop layout
  return (
    <div
      data-testid="floating-actions-panel"
      className={cn(
        "fixed z-[1000] animate-in slide-in-from-bottom-4 duration-300",
        "bg-background/95 backdrop-blur-sm border border-border/20",
        "rounded-xl shadow-lg p-4 min-w-[200px] max-w-[300px]",
        "sm:p-3 sm:min-w-[180px]",
        positionClasses[position]
      )}
      style={{
        // Динамическое позиционирование для top-right-below-header чтобы избежать конфликта с шапкой
        ...(position === 'top-right-below-header' && { top: `${topPosition}px` })
      }}
    >
      {/* Status indicator */}
      <div className="flex items-center gap-2 mb-3 text-sm text-muted-foreground">
        {getStatusIcon(currentStatus)}
        <span className="font-medium">{getStatusText(currentStatus)}</span>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-3 p-2 bg-destructive/10 border border-destructive/20 rounded text-xs text-destructive">
          {error}
        </div>
      )}

      {/* Action buttons */}
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={onCancel}
          disabled={isSaving}
          className="flex-1"
        >
          <X size={14} className="mr-1" />
          {t('records.floating_panel.cancel')}
        </Button>
        <Button
          size="sm"
          onClick={onSave}
          disabled={!hasChanges || isSaving}
          className="flex-1"
        >
          <Save size={14} className="mr-1" />
          {t('records.floating_panel.save')}
        </Button>
      </div>

      {/* Keyboard shortcuts hint */}
      <div className="mt-2 pt-2 border-t border-border/20 text-xs text-muted-foreground">
        <div>{t('records.floating_panel.shortcut_save')}</div>
        <div>{t('records.floating_panel.shortcut_cancel')}</div>
      </div>
    </div>
  );
};